/**
 *  Vuex Rest API
 *
 *  @model Maintenance Plan
 */
import Vapi from 'vuex-rest-api'
import apiConfig from '@/config/api'
import { createApiUrl, getPaginated } from '../../helpers/general'
import { axiosInstance } from '../../plugins/axios'
import { apiDropdownsMaintenanceNotifications } from '../../api'

/**
 *  Devices on REST API
 */
const api = new Vapi({
  axios: axiosInstance,
  baseURL: apiConfig.url,
  state: {
    saving: false,
    loading: false,
    editing: false,
    dialogType: 'create',
    eventDialog: false,
    planDialog: false,
    triggerDialogPlanId: null,
    triggerDialog: false,
    selectTriggerDialog: false,
    plan: {},
    updatedPlan: {},
    plans: [],
    notification: {},
    updatedNotification: {},
    notifications: [],
    loadingNotifications: false,
    trigger: {},
    updatedTrigger: {},
    triggers: []
  }
})

/**
 *  MaintenancePlans on REST API
 *
 *  @param id String
 */
api.get({
  action: 'getMaintenancePlans',
  property: 'plans',
  path: (params) => getPaginated(params, 'maintenance-plans')
})

api.get({
  action: 'getMaintenancePlan',
  property: 'plan',
  path: (params) => createApiUrl(params, 'maintenance-plans')
})

api.put({
  action: 'updateMaintenancePlan',
  property: 'updatedPlan',
  path: ({ id }) => `/maintenance-plans/${id}`
})

api.post({
  action: 'createMaintenancePlan',
  property: 'plan',
  path: '/maintenance-plans'
})

api.delete({
  action: 'deleteMaintenancePlan',
  property: 'plan',
  path: ({ id }) => `/maintenance-plans/${id}`
})

api.get({
  action: 'getNotification',
  property: 'notification',
  path: (params) => createApiUrl(params, 'maintenance-notifications')
})

api.put({
  action: 'updateNotification',
  property: 'updatedNotification',
  path: ({ id }) => `/maintenance-notifications/${id}`
})

api.post({
  action: 'createNotification',
  property: 'notification',
  path: '/maintenance-notifications'
})

api.delete({
  action: 'deleteNotification',
  property: 'notification',
  path: ({ id }) => `/maintenance-notifications/${id}`
})

api.get({
  action: 'getTriggers',
  property: 'triggers',
  path: (params) => getPaginated(params, 'triggers')
})

api.get({
  action: 'getTrigger',
  property: 'trigger',
  path: ({ id }) => `/triggers/${id}`
})

api.put({
  action: 'updateTrigger',
  property: 'updatedTrigger',
  path: ({ id }) => `/triggers/${id}`
})

api.post({
  action: 'createTrigger',
  property: 'trigger',
  path: '/triggers'
})

api.delete({
  action: 'deleteTrigger',
  property: 'trigger',
  path: ({ id }) => `/triggers/${id}`
})

const MaintenancePlans = api.getStore({
  namespaced: true,
  createStateFn: true
})

MaintenancePlans.mutations = {
  ...MaintenancePlans.mutations,
  resetPlan (state) {
    state.plan = {}
  },
  resetTrigger (state) {
    state.trigger = {}
  },
  toggleSaving (state) {
    state.saving = !state.saving
  },
  toggleLoading (state) {
    state.loading = !state.loading
  },
  enableEditing (state) {
    state.editing = true
  },
  disableEditing (state) {
    state.editing = false
  },
  toggleAddPlanDialog (state) {
    state.planDialog = !state.planDialog
  },
  openPlanDialog (state) {
    state.planDialog = true
  },
  closePlanDialog (state) {
    state.planDialog = false
  },
  openTriggerDialog (state, { planId }) {
    state.triggerDialog = true
    state.triggerDialogPlanId = planId
  },
  openSelectTriggerDialog (state, { planId }) {
    state.selectTriggerDialog = true
    state.triggerDialogPlanId = planId
  },
  closeTriggerDialog (state) {
    state.triggerDialog = false
    state.triggerDialogPlanId = null
  },
  closeSelectTriggerDialog (state) {
    state.selectTriggerDialog = false
  },
  loadingNotifications (state, { value }) {
    state.loadingNotifications = value
  },
  setDialogType (state, { type }) {
    if (type === 'create') {
      state.plan = {}
    }
    state.dialogType = type
  },
  setNotifications (state, { notifications }) {
    state.notifications = notifications
  }
}

MaintenancePlans.actions = {
  ...MaintenancePlans.actions,

  filterArray ({ commit }, { array = [], filters = {} }) {
    const filterKeys = Object.keys(filters).sort()

    // Check if any filter is selected
    if (!filterKeys.length) {
      // No filter selected, returning all items
      return array
    }

    // For each filter push filtered items to a new array
    return filterKeys.map(key => array.filter(item => filters[key](item))).flat()
  },

  async getNotifications ({ rootState, commit }, user) {
    commit('loadingNotifications', { value: true })
    const { data } = await apiDropdownsMaintenanceNotifications()

    await commit('setNotifications', { notifications: data.data })
    await commit('loadingNotifications', { value: false })
  }
}

export default MaintenancePlans
